import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, X, Save, Package, AlertTriangle, Upload, Image, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_OPTIONS = [
  { value: 'grain_free', label: 'Grain Free' },
  { value: 'hydrolysed', label: 'Hydrolysed' },
  { value: 'superfoods', label: 'Superfoods' },
  { value: 'functional_treats', label: 'Functional Treats' },
];

const LIFESTAGE_OPTIONS = [
  { value: 'puppy', label: 'Puppy' },
  { value: 'adult', label: 'Adult' },
  { value: 'senior', label: 'Senior' },
];

const BREED_SIZE_OPTIONS = [
  { value: 'small', label: 'Small Breed' },
  { value: 'large', label: 'Large Breed' },
  { value: 'all', label: 'All Breeds' },
];

const TAG_OPTIONS = [
  'everyday_feeding', 'digestive_support', 'meat_based', 'novel_protein',
  'fish_based', 'skin_coat', 'joint_care_pack', 'weight_control',
  'hydrolysed', 'dental_support', 'high_protein', 'senior', 'puppy', 
  'small_breed', 'large_breed', 'immune_support', 'calming_support'
];

const emptyProduct = {
  name: '',
  description: '',
  type: 'grain_free',
  lifestage: 'adult',
  breed_size: 'all',
  image_url: '',
  sku: '',
  tags: [],
  stock: 100,
  ingredients: '',
  analytical_constituents: '',
  benefits: '',
};

export const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(emptyProduct);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : null;
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const headers = getAuthHeader();
      if (!headers) return;
      const response = await axios.get(`${API}/admin/products`, { headers });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please use JPEG, PNG, WebP, or GIF');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploading(true);
    try {
      const headers = getAuthHeader();
      if (!headers) return;

      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/admin/upload-image`, formDataUpload, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
      });

      // Set the uploaded image URL
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      setFormData((prev) => ({ ...prev, image_url: imageUrl }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const headers = getAuthHeader();
      if (!headers) return;

      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, formData, { headers });
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/admin/products`, formData, { headers });
        toast.success('Product created successfully');
      }

      setShowForm(false);
      setEditingProduct(null);
      setFormData(emptyProduct);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      type: product.type,
      lifestage: product.lifestage,
      breed_size: product.breed_size,
      image_url: product.image_url || '',
      sku: product.sku || '',
      tags: product.tags || [],
      stock: product.stock ?? 100,
      ingredients: product.ingredients || '',
      analytical_constituents: product.analytical_constituents || '',
      benefits: product.benefits || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    try {
      const headers = getAuthHeader();
      if (!headers) return;

      await axios.delete(`${API}/admin/products/${productId}`, { headers });
      toast.success('Product deleted');
      setDeleteConfirm(null);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleTagToggle = (tag) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter((t) => t !== tag)
        : [...prev.tags, tag],
    }));
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingProduct(null);
    setFormData(emptyProduct);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0FA49C]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-[#222]">Product Management</h2>
          <p className="text-sm text-[#666]">{products.length} products total</p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-[#0FA49C] hover:bg-[#0d8f88] text-white flex items-center gap-2"
          data-testid="add-product-btn"
        >
          <Plus className="w-4 h-4" />
          Add Product
        </Button>
      </div>

      {/* Product Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={handleCancel}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg font-bold text-[#222]">
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </h3>
                <button onClick={handleCancel} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* SKU & Name */}
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#666] mb-1">SKU *</label>
                    <Input
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value.toUpperCase() })}
                      placeholder="ABC"
                      required
                      disabled={!!editingProduct}
                      className="uppercase"
                      data-testid="product-sku-input"
                    />
                  </div>
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-[#666] mb-1">Name *</label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Product name"
                      required
                      data-testid="product-name-input"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-[#666] mb-1">Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Product description"
                    required
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0FA49C] focus:border-transparent resize-none"
                    data-testid="product-description-input"
                  />
                </div>

                {/* Type, Lifestage, Breed Size */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#666] mb-1">Type *</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0FA49C]"
                      data-testid="product-type-select"
                    >
                      {TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#666] mb-1">Lifestage *</label>
                    <select
                      value={formData.lifestage}
                      onChange={(e) => setFormData({ ...formData, lifestage: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0FA49C]"
                      data-testid="product-lifestage-select"
                    >
                      {LIFESTAGE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#666] mb-1">Breed Size *</label>
                    <select
                      value={formData.breed_size}
                      onChange={(e) => setFormData({ ...formData, breed_size: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0FA49C]"
                      data-testid="product-breedsize-select"
                    >
                      {BREED_SIZE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Image Upload & Stock */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-[#666] mb-1">Product Image</label>
                    
                    {/* Upload Button */}
                    <div className="flex gap-2 mb-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageUpload}
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        data-testid="product-image-upload"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="flex items-center gap-2"
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Upload Image
                          </>
                        )}
                      </Button>
                      <span className="text-xs text-[#999] self-center">or enter URL below</span>
                    </div>
                    
                    {/* URL Input */}
                    <Input
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      placeholder="https://... or upload above"
                      data-testid="product-image-input"
                    />
                    <p className="text-xs text-[#999] mt-1">Max 5MB • JPEG, PNG, WebP, GIF</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#666] mb-1">Stock Level *</label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.stock}
                      onChange={(e) => setFormData({ ...formData, stock: parseInt(e.target.value) || 0 })}
                      required
                      data-testid="product-stock-input"
                    />
                    {formData.stock === 0 && (
                      <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Won&apos;t show to customers
                      </p>
                    )}
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-[#666] mb-2">Tags</label>
                  <div className="flex flex-wrap gap-2">
                    {TAG_OPTIONS.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => handleTagToggle(tag)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          formData.tags.includes(tag)
                            ? 'bg-[#0FA49C] text-white'
                            : 'bg-gray-100 text-[#666] hover:bg-gray-200'
                        }`}
                        data-testid={`tag-${tag}`}
                      >
                        {tag.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Ingredients, Analytical Constituents, Benefits */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-bold text-[#222] mb-3">Product Details (Accordion on Card)</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#666] mb-1">Ingredients</label>
                      <textarea
                        value={formData.ingredients}
                        onChange={(e) => setFormData({ ...formData, ingredients: e.target.value })}
                        placeholder="e.g. Freshly prepared chicken (30%), sweet potato, peas, chicken fat..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0FA49C] focus:border-transparent resize-none text-sm"
                        data-testid="product-ingredients-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#666] mb-1">Analytical Constituents</label>
                      <textarea
                        value={formData.analytical_constituents}
                        onChange={(e) => setFormData({ ...formData, analytical_constituents: e.target.value })}
                        placeholder="e.g. Protein 26%, Fat 14%, Fibre 3%, Ash 8%..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0FA49C] focus:border-transparent resize-none text-sm"
                        data-testid="product-analytical-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#666] mb-1">Benefits</label>
                      <textarea
                        value={formData.benefits}
                        onChange={(e) => setFormData({ ...formData, benefits: e.target.value })}
                        placeholder="e.g. Supports healthy digestion, promotes shiny coat, joint support..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0FA49C] focus:border-transparent resize-none text-sm"
                        data-testid="product-benefits-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Preview Image */}
                {formData.image_url && (
                  <div>
                    <label className="block text-sm font-medium text-[#666] mb-2">Image Preview</label>
                    <div className="relative inline-block">
                      <img
                        src={formData.image_url}
                        alt="Preview"
                        className="w-32 h-32 object-cover rounded-lg border shadow-sm"
                        onError={(e) => { 
                          e.target.src = ''; 
                          e.target.parentElement.innerHTML = '<div class="w-32 h-32 bg-gray-100 rounded-lg border flex items-center justify-center text-gray-400 text-xs">Failed to load</div>';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image_url: '' })}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-sm"
                        title="Remove image"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-[#0FA49C] hover:bg-[#0d8f88] text-white flex items-center justify-center gap-2"
                    data-testid="save-product-btn"
                  >
                    {saving ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        {editingProduct ? 'Update' : 'Create'}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-bold text-[#222]">Delete Product?</h3>
                  <p className="text-sm text-[#666]">This action cannot be undone.</p>
                </div>
              </div>
              <p className="text-sm text-[#666] mb-6">
                Are you sure you want to delete <strong>{deleteConfirm.name}</strong> ({deleteConfirm.sku})?
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleDelete(deleteConfirm.id)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-delete-btn"
                >
                  Delete
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Products Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#666] uppercase">Product</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#666] uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#666] uppercase">Lifestage</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#666] uppercase">Tags</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[#666] uppercase">Stock</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#666] uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {products.map((product) => (
                <tr key={product.id} className={`hover:bg-gray-50 ${product.stock === 0 ? 'bg-red-50/50' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-10 h-10 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                          <Package className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-[#222] text-sm">{product.name}</p>
                        <p className="text-xs text-[#0FA49C] font-medium">{product.sku}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      product.type === 'hydrolysed' 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {product.type.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-[#666] capitalize">{product.lifestage}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {(product.tags || []).slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 bg-gray-100 rounded text-xs text-[#666]"
                        >
                          {tag.replace(/_/g, ' ')}
                        </span>
                      ))}
                      {(product.tags || []).length > 3 && (
                        <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-[#666]">
                          +{product.tags.length - 3}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      product.stock === 0
                        ? 'bg-red-100 text-red-700'
                        : product.stock < 10
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {product.stock === 0 ? 'Out of Stock' : product.stock}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleEdit(product)}
                        className="p-2 hover:bg-gray-100 rounded-lg text-[#666] hover:text-[#0FA49C] transition-colors"
                        title="Edit"
                        data-testid={`edit-${product.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(product)}
                        className="p-2 hover:bg-red-50 rounded-lg text-[#666] hover:text-red-600 transition-colors"
                        title="Delete"
                        data-testid={`delete-${product.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
